import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useMemo, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { GridCheckbox } from '~/components/GridCheckbox';
import { SelectableGridItem } from '~/components/cards/styles';
import { GridCard } from '~/components/media/card';
import { useLink } from '~/hooks/link';
import type {
  DesignList__Design as Design,
  DesignList__DesignFolder as Folder,
} from '../DesignList.generated';
import { StudioCard } from './StudioCard';
import { StudioFolderCard } from './StudioFolderCard';
import { StudioMenu } from './StudioMenu';

interface StudioGridItemProps {
  item: Folder | Design;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedIds: GridRowSelectionModel;
}

export const StudioGridItem = ({ item, onCheck, selectedIds }: StudioGridItemProps) => {
  const { t } = useTranslation();
  const link = useLink();
  const location = useLocation();

  const selectionId = useMemo(
    () => (item.__typename === 'DesignFolder' ? `DesignFolder-${item.id}` : item.id),
    [item],
  );
  const selected = selectedIds.includes(selectionId);

  return (
    <SelectableGridItem item xs={12} sm={6} md={3} lg={2} key={selectionId} selected={selected}>
      <GridCard>
        {item.__typename === 'DesignFolder' ? (
          <StudioFolderCard designFolder={item} />
        ) : (
          <>
            <GridCheckbox
              checked={selected}
              inputProps={{ 'aria-label': t('common.actions.select_item', { name: item.name }) }}
              onChange={(event) => {
                if (event.target.checked) {
                  return onCheck([...selectedIds, selectionId]);
                }
                onCheck(selectedIds.filter((x) => x !== selectionId));
              }}
            />
            <StudioCard
              design={item}
              to={{
                ...link(`/studio/${item.designFolderId}/design/${item.id}`),
                search: location.search,
              }}
            >
              <StudioMenu item={item} />
            </StudioCard>
          </>
        )}
      </GridCard>
    </SelectableGridItem>
  );
};
